/**
 * MarketAssessment - Combined UpliftScorecard + RiskBandVisualization
 *
 * Tabbed card that consolidates two previously separate full-width components
 * into a single card with "Uplift Position" and "Risk-Adjusted Returns" tabs.
 * Reduces page scroll while keeping all data accessible.
 */

import { useState } from 'react';
import type { UpliftPosition, RiskAdjustedReturns } from '../../types/calculations';
import { UpliftScorecard } from './UpliftScorecard';
import { RiskBandVisualization } from './RiskBandVisualization';

interface MarketAssessmentProps {
  uplift?: UpliftPosition | null;
  riskReturns?: RiskAdjustedReturns | null;
}

type Tab = 'uplift' | 'risk';

export function MarketAssessment({ uplift, riskReturns }: MarketAssessmentProps) {
  const hasUplift = !!uplift;
  const hasRisk = !!riskReturns;

  // Default to whichever tab has data, preferring uplift
  const [activeTab, setActiveTab] = useState<Tab>(hasUplift ? 'uplift' : 'risk');

  // If neither has data, don't render
  if (!hasUplift && !hasRisk) return null;

  // If only one has data, render that one directly without tabs
  if (hasUplift && !hasRisk) {
    return <UpliftScorecard uplift={uplift!} />;
  }
  if (!hasUplift && hasRisk) {
    return <RiskBandVisualization riskReturns={riskReturns!} />;
  }

  return (
    <div className="rounded-xl border border-slate-dark bg-charcoal overflow-hidden">
      {/* Tab Header */}
      <div className="flex border-b border-slate-dark">
        <button
          type="button"
          onClick={() => setActiveTab('uplift')}
          className={`flex-1 px-4 py-3 text-sm font-medium uppercase tracking-wider transition-colors ${
            activeTab === 'uplift'
              ? 'text-gold border-b-2 border-gold bg-gold/5'
              : 'text-stone-700 hover:text-stone-300'
          }`}
        >
          Uplift Position
          {uplift && (
            <span className={`ml-2 text-sm font-mono ${
              uplift.composite_score >= 70 ? 'text-emerald' :
              uplift.composite_score >= 50 ? 'text-gold' : 'text-crimson'
            }`}>
              {uplift.letter_grade} ({uplift.composite_score.toFixed(0)})
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('risk')}
          className={`flex-1 px-4 py-3 text-sm font-medium uppercase tracking-wider transition-colors ${
            activeTab === 'risk'
              ? 'text-gold border-b-2 border-gold bg-gold/5'
              : 'text-stone-700 hover:text-stone-300'
          }`}
        >
          Risk-Adjusted Returns
        </button>
      </div>

      {/* Tab Content */}
      <div className="p-5">
        {activeTab === 'uplift' && uplift && (
          <UpliftScorecard uplift={uplift} />
        )}
        {activeTab === 'risk' && riskReturns && (
          <RiskBandVisualization riskReturns={riskReturns} />
        )}
      </div>
    </div>
  );
}
